import api from 'api';
import config from 'config';
import type { TalkBeforeCancelForm } from 'redux/modules/subscriptions.types';
import { encodeUrlParams } from 'utils/url-params';
import type {
  BillingPlan,
  CreatePaymentPayload,
  InvoiceDetail,
  PriceQuote,
  PriceQuoteParams,
  StripeInvoice,
  SubscriptionData,
  SubscriptionWriteResult,
  UpdatePaymentMethodPayload,
  UpdatePlanPayload,
  UpdateSeatsResult,
} from './subscription.types';

const subscriptionApi = {
  saveTalkBeforeCancelForm: async (payload: TalkBeforeCancelForm): Promise<{ ok: boolean }> => {
    const baseUrl = config.isProduction ? 'https://actions.memory.ai' : 'http://localhost:9000';
    const url = `${baseUrl}/.netlify/functions/timely_cancel_subscription_callback`;
    const response = await fetch(url, { method: 'post', body: JSON.stringify(payload) });
    return response.json();
  },
  cancelSubscriptionAndRedirect: (): Promise<unknown> => {
    return api.delete(`settings/subscription.json`);
  },

  fetchSubscription: (): Promise<SubscriptionData> => {
    return api.get('subscription.json');
  },
  fetchInvoices: (): Promise<Array<StripeInvoice>> => {
    return api.get('subscription/invoices.json');
  },
  /** Current, non-custom plans for both billing cycles. */
  fetchPlans: (): Promise<Array<BillingPlan>> => {
    return api.get('plans.json', { accountPrefix: false });
  },
  fetchPriceQuote: (params: PriceQuoteParams): Promise<PriceQuote> => {
    return api.get(`subscription/price.json${encodeUrlParams({ ...params })}`);
  },
  updatePlan: (payload: UpdatePlanPayload): Promise<SubscriptionWriteResult> => {
    return api.put('subscription/plan.json', payload);
  },
  /** `extraSeats` is the number of seats on top of the plan's included seats. */
  updateSeats: (
    extraSeats: number
  ): Promise<UpdateSeatsResult | Extract<SubscriptionWriteResult, { redirect_url: string }>> => {
    return api.put('subscription/seats.json', { seats: extraSeats });
  },
  createPayment: (payload: CreatePaymentPayload): Promise<SubscriptionWriteResult> => {
    return api.post('subscription/payment.json', payload);
  },
  updatePaymentMethod: (payload: UpdatePaymentMethodPayload): Promise<SubscriptionWriteResult> => {
    return api.put('subscription/payment_method.json', payload);
  },
  /** Keeps the current plan after a plan change was scheduled for the end of the cycle. */
  rollbackDowngrade: (): Promise<SubscriptionWriteResult> => {
    return api.delete('subscription/downgrade.json');
  },
  recover: (): Promise<SubscriptionWriteResult> => {
    return api.post('subscription/recover.json');
  },
  updateInvoiceDetails: (
    invoiceDetail: Partial<InvoiceDetail>
  ): Promise<{ status: 'success'; invoice_detail: InvoiceDetail }> => {
    return api.put('subscription/invoice_details.json', { invoice_detail: invoiceDetail });
  },
};

export default subscriptionApi;
